import React from 'react';
import {
  StepCard,
  BulletList,
  NumberedList,
  Callout,
  Connector,
  VideoIcon,
  PhoneIcon,
  PhoneCallIcon,
  LockIcon,
  CalendarIcon,
} from './guide_primitives';

import OptionTabs from './option_tabs';

import styles from './index.module.css';

export const MENTOR_GUIDE_EYEBROW = 'How Calls Work';
export const MENTOR_GUIDE_INTRO = 'How call requests work';
export const MENTOR_GUIDE_INTRO_SUB =
  "Here's what to expect when someone requests a call. The process is simple, flexible, and designed to fit your schedule.";
export const MENTOR_VIDEO_URL =
  'https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/phone_call_mentor.mp4';

function VideoCard() {
  return (
    <div className={styles.video}>
      <div className={styles.video_head}>
        <span className={styles.video_dot} aria-hidden='true' />
        <p className={styles.video_head_label}>Watch this first</p>
      </div>
      <div className={styles.video_body}>
        <video className={styles.video_media} src={MENTOR_VIDEO_URL} controls playsInline preload='metadata' />
      </div>
    </div>
  );
}

export default function MentorGuide() {
  return (
    <>
      <StepCard
        variant='white'
        icon={<VideoIcon />}
        label='Step 1'
        title='Start with a quick overview'
        description='This short video walks through the mentor call experience from beginning to end. THen explore the steps below at your own pace.'
        aside={<VideoCard />}
      />

      <Connector label='RECEIVING A CALL REQUEST' />

      <StepCard
        variant='white'
        icon={<PhoneIcon />}
        label='Step 2'
        title='Someone requests a call'
        description="When someone would like to speak with you, they'll submit a call request through the platform. You'll receive a text message and a notification in the platform."
        aside={
          <>
            <h3 className={styles.step_title}>Key things to know:</h3>
            <BulletList
              items={[
                'Call requests are sent between 8:00 AM and 8:00 PM in your local time zone.',
                "You'll have 15 minutes to accept or decline the request.",
                'Your personal phone number is never shared.',
                'You can choose to connect right away or schedule a time for later.',
              ]}
            />
          </>
        }
        callout={
          <Callout
            title="Can't respond right away?"
            body='Nothing bad happens. The request goes away on its own. The patient gets a text so they know.'
          />
        }
      />

      <Connector label="HOW YOU'D LIKE TO CONNECT" />

      <OptionTabs
        options={[
          {
            kicker: 'Option 1',
            name: 'Connect right away',
            icon: <PhoneCallIcon />,
            content: (
              <>
                <NumberedList
                  items={[
                    'Accept the request within 15 minutes.',
                    'Your phone will ring. Press 1 to join the call.',
                    'The requester will be connected.',
                    'You can begin your conversation and share your personal experience.',
                  ]}
                />
                <Callout
                  title='Miss the call?'
                  body="That's okay. The requester will be notified that the connection wasn't completed, and you don't need to do anything else."
                />
              </>
            ),
          },
          {
            kicker: 'Option 2',
            name: 'Schedule for later',
            icon: <CalendarIcon />,
            content: (
              <>
                <NumberedList
                  items={[
                    'The requester can suggest several times that work for them.',
                    'Choose one of those times, suggest alternatives, or decline the request.',
                    "You'll receive reminders before the scheduled call.",
                    'At the scheduled time, your phone will ring. Press 1 to join.',
                  ]}
                />
                <Callout
                  title='Need a different time?'
                  body='Simply suggest alternative availability. You can work together to find a time that fits both schedules.'
                />
              </>
            ),
          },
        ]}
      />

      <Connector label='DURING THE CONVERSATION' />

      <StepCard
        variant='white'
        icon={<LockIcon />}
        label='Step 3'
        title='Have the conversation'
        description="Once connected, you'll speak by phone just like any regular call. Your phone number remains private throughout the conversation."
        footerTitle='During the conversation'
        footerDescription={
          <>
            Your role is to share your personal experience and perspective. You are not expected to provide medical
            advice, answer clinical questions, or recommend treatments. If someone asks a medical question, encourage
            them to speak with their healthcare team.
          </>
        }
        subFooterTitle='Reminder'
        subFooterDescription='Mentors share their own experiences and perspectives. They do not provide medical advice, treatment recommendations, or clinical guidance. Participants should always consult their healthcare team regarding medical questions or treatment decisions.'
        aside={
          <>
            <h3 className={styles.step_title}>Key things to know:</h3>
            <BulletList
              items={[
                'No special app or equipment is required.',
                'Most conversations last about 15-30 minutes, but there is no required call length.',
                'Either person may end the conversation at any time.',
                "You're always in control of your participation.",
              ]}
            />
          </>
        }
      />

      <div className='marg-top-20'>
        <Callout
          title='YOUR PRIVACY IS PROTECTED'
          body='Your personal phone number is never shared. All calls are routed securely through the platform.'
        />
      </div>
    </>
  );
}
